import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { AiModelsCatalogModel } from '@/domain/models/ai-model.js';
import type { ClientModel } from '@/domain/models/client.js';
import { AiCritiqueSettingsHelp, SlaFormSection, resolveAiModelLabel } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { AiExtraPromptField } from '@/presentation/pages/projects/project-form/components/ai-extra-prompt-field.js';
import { AiModelOverrideField } from '@/presentation/pages/projects/project-form/components/ai-model-override-field.js';
import { SlaField } from '@/presentation/pages/projects/project-form/components/sla-field.js';
import type { FormState } from '@/presentation/pages/projects/project-form/utils/form-state.js';

interface Props {
    form: FormState;
    onField: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
    selectedClient: ClientModel | null;
    aiModelsCatalog: AiModelsCatalogModel | null;
    aiModelsLoading: boolean;
}

export function SlasTab({ form, onField, selectedClient, aiModelsCatalog, aiModelsLoading }: Props) {
    const { translate } = useApp();
    const inheritedModelLabel = resolveAiModelLabel(aiModelsCatalog, selectedClient?.aiModel);

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
            <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
                    {translate('projects.slas')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    {translate('projects.slasDesc')}
                </Typography>
                <Stack spacing={2.5}>
                    <SlaFormSection title={translate('slaSettings.flowDeadlinesTitle')} description={translate('slaSettings.flowDeadlinesDesc')}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <SlaField
                                    label={translate('projects.slaEstimateEf')}
                                    overrideLabel={translate('projects.override')}
                                    inheritedFromLabel={translate('projects.inheritedFromClient')}
                                    overrideChecked={form.overrideSlaEstimateEf}
                                    onToggleOverride={(checked) => onField('overrideSlaEstimateEf', checked)}
                                    value={form.slaHoursEstimateEf}
                                    onChangeValue={(v) => onField('slaHoursEstimateEf', v)}
                                    inheritValue={selectedClient?.slaHoursEstimateEf ?? '—'}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <SlaField
                                    label={translate('projects.slaStartDev')}
                                    overrideLabel={translate('projects.override')}
                                    inheritedFromLabel={translate('projects.inheritedFromClient')}
                                    overrideChecked={form.overrideSlaStartDev}
                                    onToggleOverride={(checked) => onField('overrideSlaStartDev', checked)}
                                    value={form.slaHoursStartDev}
                                    onChangeValue={(v) => onField('slaHoursStartDev', v)}
                                    inheritValue={selectedClient?.slaHoursStartDev ?? '—'}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <SlaField
                                    label={translate('projects.slaAnalyzeBug')}
                                    overrideLabel={translate('projects.override')}
                                    inheritedFromLabel={translate('projects.inheritedFromClient')}
                                    overrideChecked={form.overrideSlaAnalyzeBug}
                                    onToggleOverride={(checked) => onField('overrideSlaAnalyzeBug', checked)}
                                    value={form.slaHoursAnalyzeBug}
                                    onChangeValue={(v) => onField('slaHoursAnalyzeBug', v)}
                                    inheritValue={selectedClient?.slaHoursAnalyzeBug ?? '—'}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <SlaField
                                    label={translate('projects.slaAnalyzeMinor')}
                                    overrideLabel={translate('projects.override')}
                                    inheritedFromLabel={translate('projects.inheritedFromClient')}
                                    overrideChecked={form.overrideSlaAnalyzeMinor}
                                    onToggleOverride={(checked) => onField('overrideSlaAnalyzeMinor', checked)}
                                    value={form.slaHoursAnalyzeMinor}
                                    onChangeValue={(v) => onField('slaHoursAnalyzeMinor', v)}
                                    inheritValue={selectedClient?.slaHoursAnalyzeMinor ?? '—'}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <SlaField
                                    label={translate('projects.slaValidateDoc')}
                                    overrideLabel={translate('projects.override')}
                                    inheritedFromLabel={translate('projects.inheritedFromClient')}
                                    overrideChecked={form.overrideSlaDaysValidateDoc}
                                    onToggleOverride={(checked) => onField('overrideSlaDaysValidateDoc', checked)}
                                    value={form.slaDaysValidateDoc}
                                    onChangeValue={(v) => onField('slaDaysValidateDoc', v)}
                                    inheritValue={selectedClient?.slaDaysValidateDoc ?? '—'}
                                />
                            </Grid>
                        </Grid>
                    </SlaFormSection>
                    <SlaFormSection title={translate('slaSettings.aiCritiqueTitle')} description={translate('slaSettings.aiCritiqueDesc')}>
                        <Stack spacing={2}>
                            <AiCritiqueSettingsHelp />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <SlaField
                                        label={translate('aiCritique.strictnessLabel')}
                                        overrideLabel={translate('projects.override')}
                                        inheritedFromLabel={translate('projects.inheritedFromClient')}
                                        overrideChecked={form.overrideAiStrictnessLevel}
                                        onToggleOverride={(checked) => onField('overrideAiStrictnessLevel', checked)}
                                        value={form.aiStrictnessLevel}
                                        onChangeValue={(v) => onField('aiStrictnessLevel', v)}
                                        inheritValue={selectedClient?.aiStrictnessLevel ?? '—'}
                                        helperText={translate('aiCritique.strictnessHelp')}
                                        inputProps={{ min: 1, max: 10, step: 1 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <SlaField
                                        label={translate('aiCritique.minScoreLabel')}
                                        overrideLabel={translate('projects.override')}
                                        inheritedFromLabel={translate('projects.inheritedFromClient')}
                                        overrideChecked={form.overrideSlaMinScoreAi}
                                        onToggleOverride={(checked) => onField('overrideSlaMinScoreAi', checked)}
                                        value={form.slaMinScoreAi}
                                        onChangeValue={(v) => onField('slaMinScoreAi', v)}
                                        inheritValue={selectedClient?.slaMinScoreAi ?? '—'}
                                        helperText={translate('aiCritique.minScoreHelp')}
                                        inputProps={{ step: '0.01', max: 10 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <AiModelOverrideField
                                        label={translate('projects.aiModel')}
                                        overrideLabel={translate('projects.override')}
                                        inheritedFromLabel={translate('projects.inheritedFromClient')}
                                        overrideChecked={form.overrideAiModel}
                                        onToggleOverride={(checked) => onField('overrideAiModel', checked)}
                                        value={form.aiModel}
                                        onChangeValue={(modelId) => onField('aiModel', modelId)}
                                        inheritedLabel={inheritedModelLabel}
                                        catalog={aiModelsCatalog}
                                        isLoading={aiModelsLoading}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <AiExtraPromptField
                                        label={translate('projects.aiExtraSystemPrompt')}
                                        overrideLabel={translate('projects.override')}
                                        inheritedFromLabel={translate('projects.inheritedFromClient')}
                                        helperText={translate('projects.aiExtraSystemPromptHelp')}
                                        overrideChecked={form.overrideAiExtraSystemPrompt}
                                        onToggleOverride={(checked) => onField('overrideAiExtraSystemPrompt', checked)}
                                        value={form.aiExtraSystemPrompt}
                                        onChangeValue={(v) => onField('aiExtraSystemPrompt', v)}
                                        inheritedValue={selectedClient?.aiExtraSystemPrompt || '—'}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </SlaFormSection>
                </Stack>
            </CardContent>
        </Card>
    );
}
